// RolePagesDialog.js - Dialog for assigning pages to a role
import {
  useState,
  useEffect,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControlLabel,
  Checkbox,
  Stack,
  Typography,
  Divider,
  Box,
  Alert,
  CircularProgress,
  FALLBACK_PAGES
} from './SettingsCommon';

const RolePagesDialog = ({ open, role, availablePages = FALLBACK_PAGES, onClose, onSave }) => {
  const [selectedPages, setSelectedPages] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (role) {
      setSelectedPages(role.pages || []);
    } else {
      setSelectedPages([]);
    }
    setError(null);
  }, [role, open]);

  const handleTogglePage = (pageId) => {
    setSelectedPages(prev =>
      prev.includes(pageId)
        ? prev.filter(p => p !== pageId)
        : [...prev, pageId]
    );
  };

  const handleSelectAll = () => {
    setSelectedPages(availablePages.map(p => p.id));
  };

  const handleSelectNone = () => {
    setSelectedPages([]);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);

    try {
      await onSave({ ...role, pages: selectedPages });
    } catch (err) {
      setError(err.message || 'Failed to save pages');
    } finally {
      setSaving(false);
    }
  };

  if (!role) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Manage Pages for Role: <strong>{role.label}</strong>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          Select which pages users with this role can access.
        </Typography>

        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <Button size="small" variant="outlined" onClick={handleSelectAll}>
            Select All
          </Button>
          <Button size="small" variant="outlined" onClick={handleSelectNone}>
            Select None
          </Button>
        </Stack>

        <Divider sx={{ mb: 2 }} />

        <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
          <Stack spacing={1}>
            {availablePages.map((page) => (
              <FormControlLabel
                key={page.id}
                control={
                  <Checkbox
                    checked={selectedPages.includes(page.id)}
                    onChange={() => handleTogglePage(page.id)}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1">{page.label}</Typography>
                    <Typography variant="caption" color="textSecondary">
                      {page.path}
                    </Typography>
                  </Box>
                }
              />
            ))}
          </Stack>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} /> : null}
        >
          Save Pages
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RolePagesDialog;
